export const IkeArray = {
	/**
	 *  Shallow clone a array
	 *
	 * @returns New array of elements
	 **/
	clone<T>(ary: T[] | null): T[] {
		if (ary == null) {
			return [];
		}
		return ary.slice();
	},

	/**
	 * Produces a duplicate-free version of the array.
	 *
	 * @param arr Array to iterate over.
	 * @param key Key used to identify duplicate objects.
	 */
	uniqueBy<T>(arr: T[], key: keyof T): T[] {
		if (!Array.isArray(arr)) {
			return [];
		}

		const seen: { [k: string]: boolean } = {};
		const uniqueArr: T[] = [];

		for (const item of arr) {
			if (item == null) {
				continue;
			}

			const seenKey = item[key] as any;
			if (seenKey != null && !seen[seenKey]) {
				seen[seenKey] = true;
				uniqueArr.push(item);
			}
		}

		return uniqueArr;
	},

	/** Recursively flattens an array. */
	flatten<T>(data: T[][], output: T[] = []): T[] {
		for (const ary of data) {
			if (Array.isArray(ary)) {
				IkeArray.flatten(ary as any, output);
			} else {
				output.push(ary);
			}
		}
		return output;
	},

	/**
	 * Convert an array of objects to a map with each unique value assigned to the provided key.
	 * @param arr Objects to convert into a map
	 * @param key Key inside the object to use for conversion
	 */
	toMap<T, K extends keyof T>(arr: T[], key: K): { [key: string]: T } {
		const output: { [key: string]: T } = {};
		for (const obj of arr) {
			output[String(obj[key])] = obj;
		}
		return output;
	},

	/**
	 * Convert an array of objects to a map with each array of values assigned to the provided key.
	 * @param arr Objects to convert into a map
	 * @param key Key inside the object to use for conversion
	 */
	groupBy<T, K extends keyof T>(arr: T[], key: K): { [key: string]: T[] } {
		const output: { [key: string]: T[] } = {};
		for (const obj of arr) {
			const value = String(obj[key]);
			if (output[value] == null) {
				output[value] = [obj];
			} else {
				output[value].push(obj);
			}
		}
		return output;
	},

	/**
	 * Get the values in one array which are not found in the second.
	 * Both arrays are treated as sets.
	 *
	 * @param arr1 Original Array
	 * @param arr2 Other Array
	 * @returns Values of `arr1` not in `arr2`
	 */
	difference<T = string | number>(arr1: T[], arr2: T[]): T[] {
		const store = new Set(arr2);

		const result: T[] = [];
		for (const value of arr1) {
			if (!store.has(value)) {
				result.push(value);
			}
		}

		return result;
	},

	// differenceObj<T>(arr1: T[], arr2: T[], key): T[];

	/**
	 * Creates an array of elements split into groups the length of size. If array can't be split evenly, the final chunk will be the remaining elements.
	 *
	 * @param array The array to process.
	 * @param size The length of each chunk.
	 * @returns The new array of chunks.
	 */
	chunk<T>(array: T[], size: number): T[][] {
		const chunks: T[][] = [];

		if (array == null || array.length === 0) {
			return chunks;
		}

		const count = Math.ceil(array.length / size);
		for (let i = 0; i < count; i++) {
			const currentChunk = array.slice(i * size, (i + 1) * size);
			chunks.push(currentChunk);
		}

		return chunks;
	},
};
